import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { useEmployer } from 'pages/employer_onboarding/EmployerProvider'
import { usePayrollConfig } from 'pages/employer_onboarding/finalize/PayrollConfigProvider'
import { usePaySchedule } from 'pages/employer_onboarding/finalize/PayScheduleProvider'
import { useFlags } from 'pages/employer_onboarding/FlagProvider'
import { useQuote } from 'pages/employer_onboarding/QuoteProvider'
import { useGetPartnersEmbeddedOnboardingSessionStatus, useGetQuotingProspectsId, useGetQuotingProspectsProspectIdQuotes } from 'utils/web_api'
import { PayrollPayScheduleFrequency } from 'utils/web_api.schemas'

import { determineEmbeddedOnboardingStep } from './embeddedOnboardingStepHelper'
import { useEmbeddedOnboarding } from './useEmbeddedOnboarding'

const BASE_PATH = '/employers/onboarding'

export enum EmployerOnboardingPage {
  GET_STARTED = `${BASE_PATH}/welcome/get-started`,
  EMPLOYEE = `${BASE_PATH}/welcome/employee`,
  WELCOME_INTRO = `${BASE_PATH}/welcome/intro`,
  HOW_IT_WORKS = `${BASE_PATH}/welcome/how-it-works`,
  REGISTER = `${BASE_PATH}/welcome/register`,
  EMBEDDED_SET_PASSWORD = `${BASE_PATH}/welcome/embedded-set-password`,
  BASIC_INFO = `${BASE_PATH}/welcome/info`,
  SETUP_INTRO = `${BASE_PATH}/setup/intro`,
  SETUP_ALLOWANCES = `${BASE_PATH}/setup/allowances`,
  CENSUS = `${BASE_PATH}/setup/census`,
  CENSUS_EMBEDDED = `${BASE_PATH}/setup/census/embedded`,
  CENSUS_CONFIRMATION = `${BASE_PATH}/setup/census/confirm`,
  ADDITIONAL_INFO = `${BASE_PATH}/setup/additional-info`,
  BENEFITS = `${BASE_PATH}/setup/benefits`,
  NEW_BENEFITS = `${BASE_PATH}/setup/benefits/new`,
  CURRENT_BENEFITS = `${BASE_PATH}/setup/benefits/current`,
  ALLOWANCE_STRATEGY = `${BASE_PATH}/setup/strategy`,
  PENDING_RATES = `${BASE_PATH}/setup/pending-rates`,
  QUOTE = `${BASE_PATH}/quote`,
  QUOTE_REVIEW = `${BASE_PATH}/quote/review`,
  ONE_MORE_REMINDER = `${BASE_PATH}/quote/one-more-reminder`,
  NEXT_STEPS = `${BASE_PATH}/finalize/next-steps`,
  PAYROLL = `${BASE_PATH}/finalize/payroll`,
  AUTOMATIC_DEDUCTIONS = `${BASE_PATH}/finalize/payroll/automatic-deductions`,
  PAY_SCHEDULE = `${BASE_PATH}/finalize/pay-schedule`,
  REMAINING_INFO = `${BASE_PATH}/finalize/info`,
  BANKING = `${BASE_PATH}/finalize/banking`,
  COMPLETE = `${BASE_PATH}/complete`,
  RESUME = `${BASE_PATH}/resume`,
  EDIT_ALLOWANCES = `${BASE_PATH}/edit`
}

// Steps that navigate away by replacing their page on the routing history
const transientSteps = new Set([
  EmployerOnboardingPage.REGISTER,
  EmployerOnboardingPage.BASIC_INFO,
  EmployerOnboardingPage.AUTOMATIC_DEDUCTIONS,
  EmployerOnboardingPage.PAY_SCHEDULE,
  EmployerOnboardingPage.RESUME,
  EmployerOnboardingPage.EMBEDDED_SET_PASSWORD,
  EmployerOnboardingPage.EDIT_ALLOWANCES])

interface UseOnboardingStepResult {
  resumeStep: string
  navigateToPreviousStep: () => void
  navigateToNextStep: () => void
  navigateToStep: (step: EmployerOnboardingPage) => void
}

/**
 * Hook that provides navigation for the employer onboarding flow. This accounts for any necessary auth or page skips.
 *
 * @returns methods to direct employer onboarding navigation
 */
export const useOnboardingStep = (): UseOnboardingStepResult => {
  const { isEmbedded } = useEmbeddedOnboarding()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { employer } = useEmployer()
  const { paySchedules } = usePaySchedule()
  const { payrollConfig } = usePayrollConfig()
  const { quote, shouldShowPendingPricesWarning } = useQuote()
  const { flags } = useFlags()
  const skipAllowanceStrategyPage = flags.enableNewPartnerQuotingPage === true
  const { data: prospect } = useGetQuotingProspectsId(employer?.quoting_prospect_id || '')
  const sessionStatusQuery = useGetPartnersEmbeddedOnboardingSessionStatus({
    query: {
      enabled: !!isEmbedded,
    },
  })
  const { data: quotes } = useGetQuotingProspectsProspectIdQuotes(employer?.quoting_prospect_id || '', undefined, {
    query: {
      enabled: !!prospect?.id,
    },
  })

  const resumeStep = useMemo(() => {
    if (isEmbedded) {
      return determineEmbeddedOnboardingStep({
        completeEmployerProfile: employer?.employer_profile_complete ?? false,
        quotes,
        sessionStatus: sessionStatusQuery.data,
      })
    }

    // employer is created on welcome intro page
    if (!employer) {
      return EmployerOnboardingPage.WELCOME_INTRO
    }

    // name is required on basic info page
    if (!employer.name) {
      return EmployerOnboardingPage.BASIC_INFO
    }

    // prospect is created on setup intro page
    if (!prospect) {
      return EmployerOnboardingPage.SETUP_INTRO
    }

    if (!prospect.has_census) {
      return EmployerOnboardingPage.CENSUS
    }

    // industry is required on additional info page
    if (!employer.industry) {
      return EmployerOnboardingPage.ADDITIONAL_INFO
    }

    // If no quotes have been generated, go to benefits page
    if (!quotes?.data?.length) {
      return EmployerOnboardingPage.BENEFITS
    }

    // If any quotes are unprocessed, go to allowance strategy page (which triggers generation)
    if (quotes?.data?.some(quote => quote.status === 'unprocessed')) {
      return EmployerOnboardingPage.ALLOWANCE_STRATEGY
    }

    // This means no quote has been accepted yet
    if (!employer.has_current_ichra_config) {
      return EmployerOnboardingPage.QUOTE
    }

    // if neither pay schedule nor payroll connection is set, go to payroll page
    if (!employer.has_pay_schedule && !employer.has_payroll_connection) {
      return EmployerOnboardingPage.PAYROLL
    }

    // all info required for employer_profile_complete is set on remaining info page
    if (!employer.employer_profile_complete) {
      return EmployerOnboardingPage.REMAINING_INFO
    }

    // banking is final step if all other info is set
    return EmployerOnboardingPage.BANKING
  }, [employer, prospect, quotes, sessionStatusQuery.data, isEmbedded])

  const navigateToStep = (step: EmployerOnboardingPage) => {
    const replace = transientSteps.has(currentStep)
    if (!replace) {
      stepNavigationStack.push(step)
    }

    replace ? navigate(step, { replace }) : navigate(step)
  }

  const currentStep = pathname as EmployerOnboardingPage
  if (!Object.values(EmployerOnboardingPage).includes(currentStep)) {
    return {
      resumeStep,
      navigateToPreviousStep: () => {},
      navigateToNextStep: () => {},
      navigateToStep
    }
  }

  const firstStep = isEmbedded
    ? EmployerOnboardingPage.HOW_IT_WORKS
    : EmployerOnboardingPage.GET_STARTED

  const nextSteps = useMemo(() => {
    const showQuoteWarning = shouldShowPendingPricesWarning && quote?.preliminary

    return {
      [EmployerOnboardingPage.GET_STARTED]: EmployerOnboardingPage.WELCOME_INTRO,
      [EmployerOnboardingPage.EMPLOYEE]: undefined,
      [EmployerOnboardingPage.WELCOME_INTRO]: EmployerOnboardingPage.HOW_IT_WORKS,
      [EmployerOnboardingPage.HOW_IT_WORKS]: isEmbedded
        ? EmployerOnboardingPage.SETUP_INTRO
        : EmployerOnboardingPage.REGISTER,
      [EmployerOnboardingPage.REGISTER]: EmployerOnboardingPage.BASIC_INFO,
      [EmployerOnboardingPage.EMBEDDED_SET_PASSWORD]: EmployerOnboardingPage.NEXT_STEPS,
      [EmployerOnboardingPage.BASIC_INFO]: EmployerOnboardingPage.SETUP_INTRO,
      [EmployerOnboardingPage.SETUP_INTRO]: EmployerOnboardingPage.SETUP_ALLOWANCES,
      [EmployerOnboardingPage.SETUP_ALLOWANCES]: isEmbedded
        ? EmployerOnboardingPage.CENSUS_EMBEDDED
        : EmployerOnboardingPage.CENSUS,
      [EmployerOnboardingPage.CENSUS]: EmployerOnboardingPage.CENSUS_CONFIRMATION,
      [EmployerOnboardingPage.CENSUS_EMBEDDED]: EmployerOnboardingPage.CENSUS_CONFIRMATION,
      // Checking employer.industry is a naive check on whether additional info on the next page is already provided
      [EmployerOnboardingPage.CENSUS_CONFIRMATION]: employer?.industry
        ? EmployerOnboardingPage.BENEFITS
        : EmployerOnboardingPage.ADDITIONAL_INFO,
      [EmployerOnboardingPage.ADDITIONAL_INFO]: EmployerOnboardingPage.BENEFITS,
      [EmployerOnboardingPage.BENEFITS]: shouldShowPendingPricesWarning
        ? EmployerOnboardingPage.PENDING_RATES
        : prospect?.offers_health_insurance_today
          ? EmployerOnboardingPage.CURRENT_BENEFITS
          : isEmbedded
            ? (skipAllowanceStrategyPage ? EmployerOnboardingPage.QUOTE : EmployerOnboardingPage.ALLOWANCE_STRATEGY)
            : EmployerOnboardingPage.NEW_BENEFITS,
      [EmployerOnboardingPage.NEW_BENEFITS]: EmployerOnboardingPage.ALLOWANCE_STRATEGY,
      [EmployerOnboardingPage.CURRENT_BENEFITS]: isEmbedded && skipAllowanceStrategyPage
        ? EmployerOnboardingPage.QUOTE
        : EmployerOnboardingPage.ALLOWANCE_STRATEGY,
      [EmployerOnboardingPage.ALLOWANCE_STRATEGY]: EmployerOnboardingPage.QUOTE,
      [EmployerOnboardingPage.PENDING_RATES]: prospect?.offers_health_insurance_today
        ? EmployerOnboardingPage.CURRENT_BENEFITS
        : isEmbedded
          ? (skipAllowanceStrategyPage ? EmployerOnboardingPage.QUOTE : EmployerOnboardingPage.ALLOWANCE_STRATEGY)
          : EmployerOnboardingPage.NEW_BENEFITS,
      [EmployerOnboardingPage.QUOTE]: EmployerOnboardingPage.QUOTE_REVIEW,
      [EmployerOnboardingPage.QUOTE_REVIEW]: showQuoteWarning
        ? EmployerOnboardingPage.ONE_MORE_REMINDER
        : isEmbedded
          ? EmployerOnboardingPage.EMBEDDED_SET_PASSWORD
          : EmployerOnboardingPage.NEXT_STEPS,
      [EmployerOnboardingPage.ONE_MORE_REMINDER]: isEmbedded
        ? EmployerOnboardingPage.EMBEDDED_SET_PASSWORD
        : EmployerOnboardingPage.NEXT_STEPS,
      [EmployerOnboardingPage.NEXT_STEPS]: !isEmbedded
        ? EmployerOnboardingPage.PAYROLL
        : paySchedules.some(paySchedule =>
          paySchedule.frequency === PayrollPayScheduleFrequency.semi_monthly && !paySchedule.second_day
        )
          ? EmployerOnboardingPage.PAY_SCHEDULE
          : !employer?.ein || !employer?.employer_profile_complete
              ? EmployerOnboardingPage.REMAINING_INFO
              : EmployerOnboardingPage.BANKING,
      [EmployerOnboardingPage.PAYROLL]: payrollConfig?.supports_automatic_payroll_deductions
        ? EmployerOnboardingPage.AUTOMATIC_DEDUCTIONS
        : !payrollConfig?.supports_pay_schedule_sync
            ? EmployerOnboardingPage.PAY_SCHEDULE
            : EmployerOnboardingPage.REMAINING_INFO,
      [EmployerOnboardingPage.AUTOMATIC_DEDUCTIONS]: !payrollConfig?.supports_pay_schedule_sync
        ? EmployerOnboardingPage.PAY_SCHEDULE
        : EmployerOnboardingPage.REMAINING_INFO,
      [EmployerOnboardingPage.PAY_SCHEDULE]: EmployerOnboardingPage.REMAINING_INFO,
      [EmployerOnboardingPage.REMAINING_INFO]: EmployerOnboardingPage.BANKING,
      [EmployerOnboardingPage.BANKING]: EmployerOnboardingPage.COMPLETE,
      [EmployerOnboardingPage.COMPLETE]: undefined,
      [EmployerOnboardingPage.RESUME]: undefined,
      [EmployerOnboardingPage.EDIT_ALLOWANCES]: undefined,
    }
  }, [isEmbedded, shouldShowPendingPricesWarning, quote?.preliminary, prospect?.offers_health_insurance_today, payrollConfig, employer, paySchedules, skipAllowanceStrategyPage])

  const stepNavigationStack = useMemo(() => {
    const steps: EmployerOnboardingPage[] = []
    const endStep = currentStep
    let step: EmployerOnboardingPage | undefined = firstStep

    while (step !== undefined && step !== endStep) {
      const replace = transientSteps.has(step)
      if (!replace) {
        steps.push(step)
      }

      step = nextSteps[step]
    }

    return steps
  }, [nextSteps])

  const navigateToPreviousStep = () => {
    const previousStep = stepNavigationStack.pop()
    if (previousStep === EmployerOnboardingPage.COMPLETE) {
      // We somehow missed the page - navigate back instead
      navigate(-1)
    } else if (previousStep) {
      navigate(previousStep)
    }
  }

  const navigateToNextStep = () => nextSteps[currentStep] && navigateToStep(nextSteps[currentStep])

  return {
    resumeStep,
    navigateToPreviousStep,
    navigateToNextStep,
    navigateToStep
  }
}
